import { useEffect, useRef, useState, memo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import {
  getClientInitials,
  getClientLogoSrc,
} from "../../containers/settings/client/clientUtils";
import { sinceDate } from "../formatted/FormattedDateAndTime";
import { getSettingsData } from "../../services/settings";
import {
  AVATAR_PRESETS,
  getAvatarUrl,
  readStoredAvatarSeed,
  writeStoredAvatarSeed,
} from "./avatarPresets";
import SidebarNavItem from "./SidebarNavItem";
import {
  IconCampaign,
  IconCharacterization,
  IconCollapse,
  IconDashboard,
  IconHistory,
  IconLogout,
  IconMonitoring,
  IconPerformance,
  IconSettings,
} from "./navIcons";
import styles from "./AppSidebar.module.css";
import ProgressBar from "../../ui/components/ProgressBar";
import {
  characterizationLandingPath,
  extractSettingsDocument,
  isCharacterizationNavActive,
  rememberCharacterizationViewModeFromSettings,
} from "../../containers/characterization-2/core/utils/characterizationViewMode";

const isPathActive = (pathname, path) => {
  if (path === "/") return pathname === "/" || pathname === "";
  return pathname === path || pathname.startsWith(`${path}/`);
};

const getLicenseElapsedPercent = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  const remainingRatio = (end - now) / (end - start);
  const elapsedPercent = (1 - remainingRatio) * 100;
  return Math.max(0, Math.min(100, Math.round(elapsedPercent)));
};

const AppSidebar = ({
  setSideNavExpanded,
  viewMode: viewModeProp,
  corporation,
  user,
  setCookies,
  saveUser,
  expanded: expandedProp,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const pickerRef = useRef(null);

  const viewMode = viewModeProp ?? (Cookies.get("viewMode") || "client");
  const corporationModules = (() => {
    try {
      return JSON.parse(localStorage.getItem("corporationModules") || "null");
    } catch {
      return null;
    }
  })();

  const cookieExpanded = Cookies.get("sideNavExpanded");
  const initialExpanded =
    expandedProp ??
    (cookieExpanded !== undefined ? JSON.parse(cookieExpanded) : true);

  const [expanded, setExpanded] = useState(initialExpanded);
  const [licenseEndDate, setLicenseEndDate] = useState(null);
  const [characterizationCredits, setCharacterizationCredits] = useState(null);
  const [avatarSeed, setAvatarSeed] = useState(readStoredAvatarSeed);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [characterizationViewMode, setCharacterizationViewMode] =
    useState(null);

  const monitoringModuleOn = corporationModules
    ? Boolean(corporationModules.showMonitoringModule)
    : true;
  const characterizationModuleOn = corporationModules
    ? Boolean(corporationModules.showCharacterizationModule)
    : true;
  const campaignModuleOn = corporationModules
    ? Boolean(corporationModules.showCampaignModule)
    : true;

  const monitoringEnabled = monitoringModuleOn || viewMode === "admin";
  const characterizationEnabled =
    characterizationModuleOn || viewMode === "admin";
  const campaignEnabled = campaignModuleOn || viewMode === "admin";

  const monitoringLocked = !monitoringModuleOn && viewMode === "admin";
  const characterizationLocked =
    !characterizationModuleOn && viewMode === "admin";
  const campaignLocked = !campaignModuleOn && viewMode === "admin";

  useEffect(() => {
    let cancelled = false;
    getSettingsData()
      .then((response) => {
        if (cancelled) return;
        if (response?.status >= 200 && response?.status < 400) {
          const settings = extractSettingsDocument(response.data);
          setLicenseEndDate(settings?.licenseEndDate ?? null);
          const credits = Number(settings?.characterizationCredits);
          setCharacterizationCredits(Number.isFinite(credits) ? credits : 0);
          setCharacterizationViewMode(
            rememberCharacterizationViewModeFromSettings(settings),
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLicenseEndDate(null);
          setCharacterizationCredits(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!avatarPickerOpen) return undefined;
    const onPointerDown = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setAvatarPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [avatarPickerOpen]);

  const toggleExpanded = () => {
    const next = !expanded;
    Cookies.set("sideNavExpanded", JSON.stringify(next), { secure: true });
    setExpanded(next);
    setSideNavExpanded?.(next);
    setAvatarPickerOpen(false);
  };

  const handleLogout = async () => {
    await setCookies?.(null, null, null);
    await saveUser?.(null);
    localStorage.clear();
    sessionStorage.clear();
    history.push("/login");
  };

  const selectAvatar = (seed) => {
    setAvatarSeed(seed);
    writeStoredAvatarSeed(seed);
    setAvatarPickerOpen(false);
  };

  const corporationName =
    corporation?.corporationName || corporation?.name || "";
  const logoSrc = getClientLogoSrc(corporation?.logoUrl);
  const initials = getClientInitials(corporationName);
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    Cookies.get("firstName") ||
    "";
  const displayEmail = user?.email || Cookies.get("userEmail") || "";

  const licenseDuration = licenseEndDate
    ? sinceDate(new Date(), licenseEndDate, t)
    : "—";
  const licenseProgressPercent = getLicenseElapsedPercent(
    corporation?.licenseStartDate,
    licenseEndDate,
  );

  const navItems = [
    {
      id: "dashboard",
      path: "/",
      label: t("Dashboard"),
      Icon: IconDashboard,
      visible: true,
      locked: false,
    },
    {
      id: "monitoring",
      path: "/containers",
      label: t("Monitoring"),
      Icon: IconMonitoring,
      visible: monitoringEnabled,
      locked: monitoringLocked,
    },
    {
      id: "characterization",
      path: characterizationLandingPath(characterizationViewMode),
      isActive: isCharacterizationNavActive,
      label: t("Characterizations"),
      Icon: IconCharacterization,
      visible: characterizationEnabled,
      locked: characterizationLocked,
    },
    {
      id: "performance",
      path: "/statistics",
      label: t("Performance"),
      Icon: IconPerformance,
      visible: true,
      locked: false,
    },
    {
      id: "history",
      path: "/activity",
      label: t("History"),
      Icon: IconHistory,
      visible: true,
      locked: false,
    },
    {
      id: "campaign",
      path: "/campaign",
      label: t("Sorting campaign"),
      Icon: IconCampaign,
      visible: campaignEnabled,
      locked: campaignLocked,
    },
    {
      id: "settings",
      path: "/settings",
      label: t("Settings"),
      Icon: IconSettings,
      visible: true,
      locked: false,
      section: "footer",
    },
    {
      id: "logout",
      label: t("Logout"),
      Icon: IconLogout,
      visible: true,
      locked: false,
      section: "footer",
      onClick: handleLogout,
    },
  ];

  const renderNavItem = (item) => (
    <SidebarNavItem
      key={item.id}
      label={item.label}
      icon={<item.Icon />}
      expanded={expanded}
      active={
        item.isActive
          ? item.isActive(location.pathname)
          : item.path
            ? isPathActive(location.pathname, item.path)
            : false
      }
      locked={item.locked}
      to={item.path}
      onClick={item.onClick}
    />
  );

  const mainItems = navItems.filter(
    (item) => item.visible && item.section !== "footer",
  );
  const footerItems = navItems.filter(
    (item) => item.visible && item.section === "footer",
  );

  return (
    <aside
      className={`${styles.sidebar} ${expanded ? "" : styles.sidebarCollapsed}`}
      aria-label={t("Navigation")}
    >
      <div className={styles.top}>
        <div className={styles.brandRow}>
          {expanded ? (
            <>
              <div className={styles.brandLogo} aria-hidden>
                {logoSrc ? (
                  <img src={logoSrc} alt="" />
                ) : (
                  <span className={styles.brandInitials}>{initials}</span>
                )}
              </div>
              <span className={styles.brandName}>{corporationName || "—"}</span>
              <div className={styles.collapseWrap}>
                <button
                  type="button"
                  className={styles.collapseButton}
                  onClick={toggleExpanded}
                  aria-label={t("Collapse navigation")}
                  title={t("Collapse navigation")}
                >
                  <IconCollapse />
                </button>
              </div>
            </>
          ) : (
            <div className={styles.collapseWrap}>
              <button
                type="button"
                className={`${styles.collapseButton} ${styles.collapseButtonRotated}`}
                onClick={toggleExpanded}
                aria-label={t("Expand navigation")}
                title={t("Expand navigation")}
              >
                <IconCollapse />
              </button>
              <span className={styles.tooltip}>{t("Expand navigation")}</span>
            </div>
          )}
        </div>

        <nav className={styles.nav}>{mainItems.map(renderNavItem)}</nav>
      </div>

      <div className={styles.bottom}>
        {expanded ? (
          <>
            <div className={styles.licenseCard}>
              <p className={styles.licenseTitle}>{t("License")}</p>
              <p className={styles.licenseRemaining}>
                {licenseDuration} {t("remaining")}
              </p>
              {licenseProgressPercent !== null ? (
                <div className={styles.licenseProgress}>
                  <ProgressBar value={licenseProgressPercent} />
                </div>
              ) : null}
            </div>
            {characterizationModuleOn ? (
              <div className={styles.licenseCard}>
                <p className={styles.creditsLine}>
                  <span className={styles.creditsCount}>
                    {characterizationCredits ?? "—"}
                  </span>{" "}
                  {t("characterization credits left")}
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {expanded ? <hr className={styles.divider} /> : null}

        {footerItems.map(renderNavItem)}

        {expanded ? <hr className={styles.divider} /> : null}

        <div className={styles.userSection} ref={pickerRef}>
          <button
            type="button"
            className={styles.userBlock}
            onClick={() => setAvatarPickerOpen((open) => !open)}
            aria-haspopup="listbox"
            aria-expanded={avatarPickerOpen}
            title={t("Change avatar")}
          >
            <span className={styles.avatar}>
              <img src={getAvatarUrl(avatarSeed)} alt="" />
            </span>
            {expanded ? (
              <span className={styles.userMeta}>
                <p className={styles.userName}>{displayName || "—"}</p>
                <p className={styles.userEmail}>{displayEmail || "—"}</p>
              </span>
            ) : null}
          </button>

          {avatarPickerOpen ? (
            <div className={styles.avatarPicker} role="listbox">
              <p className={styles.avatarPickerTitle}>
                {t("Choose an avatar")}
              </p>
              <div className={styles.avatarGrid}>
                {AVATAR_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    role="option"
                    aria-selected={preset.seed === avatarSeed}
                    className={`${styles.avatarOption} ${
                      preset.seed === avatarSeed
                        ? styles.avatarOptionSelected
                        : ""
                    }`}
                    onClick={() => selectAvatar(preset.seed)}
                    title={preset.label}
                  >
                    <img src={getAvatarUrl(preset.seed)} alt={preset.label} />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
};

export default memo(AppSidebar);
